import { Fragment, useMemo } from "react";

import { cn } from "@/lib/utils";

import {
  AllCategoriesIcon,
  EscapeRoomIcon,
  HauntedHayrideIcon,
  HauntedHouseIcon,
  HauntedMazeIcon,
  HauntedTrailIcon,
  HauntIcon,
  IrlIcon,
  MortyIcon,
  OtherIcon,
  PopUpIcon,
  PortableIcon,
  ScareZoneIcon,
  TabletopIcon,
  VirtualIcon,
  YardDisplayIcon,
} from "./icons";

export interface CategoryType {
  id: string;
  name: string;
  parent?: CategoryType;
  slug: string;
}

export const SLUG_TO_ICON: Record<
  string,
  React.FC<React.ComponentProps<"svg">>
> = {
  all: AllCategoriesIcon,
  "escape-room": EscapeRoomIcon,
  haunt: HauntIcon,
  "haunted-house": HauntedHouseIcon,
  "haunted-maze": HauntedMazeIcon,
  "haunted-trail": HauntedTrailIcon,
  hayride: HauntedHayrideIcon,
  irl: IrlIcon,
  morty: MortyIcon,
  online: VirtualIcon,
  other: OtherIcon,
  "pop-up": PopUpIcon,
  portable: PortableIcon,
  "scare-zone": ScareZoneIcon,
  tabletop: TabletopIcon,
  virtual: VirtualIcon,
  vr: VirtualIcon,
  "yard-display": YardDisplayIcon,
};

interface GameCategoriesProps {
  categories: CategoryType[];
  className?: string;
  withoutParent?: boolean;
}

export default function GameCategories({
  categories,
  className,
  withoutParent = false,
}: GameCategoriesProps) {
  const items = useMemo(
    () =>
      categories.reduce((accum, category) => {
        const c = [category];

        if (
          !withoutParent &&
          category.parent &&
          category.slug !== category.parent.slug
        ) {
          c.push(category.parent);
          c.reverse();
        }

        accum.push(...c);

        return accum;
      }, [] as CategoryType[]),
    [categories, withoutParent]
  );

  return (
    <ul className={cn("flex", className)}>
      {items.map((category) => {
        const Icon =
          category.slug in SLUG_TO_ICON
            ? SLUG_TO_ICON[category.slug]
            : Fragment;

        return (
          <li
            className="flex h-5.5 items-center gap-1 rounded-[11px] border border-white/20 bg-white/5 pr-2 pl-2.5 md:h-6 md:rounded-[12px]"
            key={category.id}
          >
            <Icon />
            <span className="line-clamp-1 font-medium text-11 text-pale-lavender md:text-12">
              {category.name}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
